import { useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';

import Session from '@mythos/utils/Session';
import { useShallow } from 'zustand/shallow';
import ActionStore from './ActionStore';
import ChatOverlay from './ChatOverlay';
import { PlayerColors } from './ColorPalette';
import GameView from './GameView';
import { UserWithColor } from './GraphQLHelpers';
import { playSound } from './SoundPlayer';
import { subscribeToTopic, unsubscribeFromTopic } from './stores/channelStore';
import { useGameStore } from './stores/gameStore';
import { useSessionStore } from './stores/sessionStore';
import { useUserStore } from './stores/userStore';

import { GameViewModel } from '@mythos/game/GameViewModel';
import LastTurnSFX from '../assets/cornu 2note.mp3';
import NewAgeSFX from '../assets/gong-swells-mkii-38714.mp3';
import AffirmSFX from '../assets/melodic2_affirm.wav';

function createActionStore(
  session: Session | null,
  game: GameViewModel | null,
): ActionStore | null {
  if (!session) {
    return null;
  }
  if (!game) {
    return null;
  }
  const has_session_user = game.playerStates.some(
    (player) => player.userID === session.getUserID(),
  );
  if (!has_session_user) {
    return null;
  }
  return new ActionStore(game, session.getUserID());
}

function GamePage() {
  const session = useSessionStore((state) => state.session)!;
  const gameID = useParams().gameID!;
  const { game, gameError, fetchGame, applyGameUpdate } = useGameStore(
    useShallow((state) => ({
      game: state.gameByID[gameID] || null,
      gameError: state.gameErrorByID[gameID] || null,
      fetchGame: state.fetchGame,
      applyGameUpdate: state.applyGameUpdate,
    })),
  );
  const { userByID: inputUserByID, fetchUsers } = useUserStore(
    useShallow((state) => ({
      userByID: state.userByID,
      fetchUsers: state.fetchUsers,
    })),
  );

  useEffect(() => {
    fetchGame(gameID, null);
  }, [gameID, fetchGame]);

  const actionStore = useMemo(
    () => createActionStore(session, game),
    [session, game !== null],
  );

  useEffect(() => {
    if (actionStore && game) {
      actionStore.setGame(game);
    }
  }, [actionStore, game]);

  const userIDs = useMemo(
    () => game?.playerStates.map((player) => player.userID) || [],
    [game],
  );

  const userWithColorByID: Map<string, UserWithColor> = useMemo(() => {
    if (!game) {
      return new Map();
    }
    return new Map(
      userIDs.map((userID, i) => [
        userID,
        {
          ...inputUserByID[userID]!,
          color: PlayerColors[i],
        },
      ]),
    );
  }, [userIDs, inputUserByID]);

  useEffect(() => {
    if (!game) {
      return;
    }

    const topic = getChannelTopic(session, game, gameID);
    const subscription = subscribeToTopic(topic, (message) => {
      const gameUpdate = message.data;
      console.log('received game update', gameUpdate);
      applyGameUpdate(gameUpdate);
    });
    return () => {
      unsubscribeFromTopic(subscription);
    };
  }, [session, game === null, gameID, applyGameUpdate]);
  const fetchedUserIDs = useRef(new Set<string>()).current;
  useEffect(() => {
    let missing_ids = userIDs.filter((user_id) => {
      return !inputUserByID[user_id] && !fetchedUserIDs.has(user_id);
    });
    if (missing_ids.length === 0) {
      return;
    }

    fetchUsers(missing_ids);
    missing_ids.forEach((id) => fetchedUserIDs.add(id));
  }, [inputUserByID, userIDs, fetchUsers]);

  const lastPhaseRef = useRef(game?.phase);
  const lastAgeRef = useRef(game?.age);
  const lastTurnRef = useRef(game?.turn);
  useEffect(() => {
    if (lastAgeRef.current !== undefined && lastAgeRef.current !== game?.age) {
      playSound(NewAgeSFX, 0.3);
    } else if (
      lastTurnRef.current !== undefined &&
      lastTurnRef.current !== game?.turn &&
      game?.turn === game?.turnsPerAge
    ) {
      playSound(LastTurnSFX, 0.3);
    } else if (
      lastPhaseRef.current !== undefined &&
      game?.phase !== lastPhaseRef.current
    ) {
      playSound(AffirmSFX);
    }
    lastPhaseRef.current = game?.phase;
    lastAgeRef.current = game?.age;
    lastTurnRef.current = game?.turn;
  }, [game?.phase, game?.age]);

  if (gameError) {
    return <div className="game-loading-view">Game not found</div>;
  }

  if (!game || userIDs.some((user_id) => !inputUserByID[user_id])) {
    return <div className="game-loading-view">Loading...</div>;
  }

  return (
    <>
      <GameView
        key={game.id}
        actionStore={actionStore || undefined}
        game={game}
        session={session}
        userByID={userWithColorByID}
      />
      <ChatOverlay />
    </>
  );
}

function getChannelTopic(
  session: Session | null,
  game: GameViewModel | null,
  gameID: string,
): string {
  let topicPrefix = '';
  if (session) {
    const userID = session.getUserID();
    if (game) {
      if (game.playerStates.some((player) => player.userID === userID)) {
        topicPrefix = `inbox/${userID}/`;
      }
    }
  }
  return topicPrefix + `game/${gameID}`;
}

export default GamePage;
